import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCamera,
  faCheckCircle,
  faChevronLeft,
  faChevronRight,
  faClipboardCheck,
  faClock,
  faExclamationTriangle,
  faForward,
  faImage,
  faInfoCircle,
  faRedo,
  faSave,
  faSync
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import moment from "moment";
import SubtleBadge from "components/common/SubtleBadge";
import useScheduleTokens from "./scheduleTokens";
import ScheduleBadge from "./ScheduleBadge";
import {
  AUDIT_STATE,
  MAX_AUDIT_IMAGE_BYTES,
  errorMessage,
  getAuditChecklist,
  submitAudit,
  uploadAuditImage
} from "./auditApi";

const PAGE_SIZE = 15;

const formatDate = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("MM/DD/YY") : String(value);
};

const formatQty = (n) => (Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2))));

/* ------------------------------------------------------------------ */
/* Audit state banner (INVUI-702, 705)                                 */
/*                                                                     */
/* A prompt, never a gate (U15). always_skippable comes straight from  */
/* the server (U16) and softens an overdue audit to a reminder.        */
/* ------------------------------------------------------------------ */

export const auditPresentation = (auditState) => {
  const state = auditState?.state || AUDIT_STATE.NOT_REQUIRED;
  const soft = Boolean(auditState?.alwaysSkippable);
  const due = formatDate(auditState?.dueDate);

  switch (state) {
    case AUDIT_STATE.GRACE:
      return {
        tone: "warning",
        icon: faClock,
        title: "Audit due",
        text: `Please count this patient's medications by ${due}. You can skip for now and come back before then.`,
        prompting: true,
        canSkip: true,
        tab: { label: "Due", bg: "warning" }
      };
    case AUDIT_STATE.REQUIRED:
      return soft
        ? {
            tone: "warning",
            icon: faClock,
            title: "Audit overdue — reminder only",
            text: "This audit is past due. It's set to never block, so you can skip it, but please get to it soon.",
            prompting: true,
            canSkip: true,
            tab: { label: "Overdue", bg: "warning" }
          }
        : {
            tone: "danger",
            icon: faExclamationTriangle,
            title: "Audit overdue",
            text: "This audit is past its due date. Please complete it now.",
            prompting: true,
            canSkip: false,
            tab: { label: "Overdue", bg: "danger" }
          };
    case AUDIT_STATE.CONFIRMED:
      return {
        tone: "success",
        icon: faCheckCircle,
        title: "Audit up to date",
        text: "The latest audit for this patient is complete.",
        prompting: false,
        canSkip: false,
        tab: null
      };
    default:
      return {
        tone: "secondary",
        icon: faInfoCircle,
        title: "No audit due",
        text: "There's no audit waiting for this patient. You can still run one if you need to.",
        prompting: false,
        canSkip: false,
        tab: null
      };
  }
};

const AuditStateBanner = ({ auditState, loading, onRefresh, skipped, onSkip, onStart, started }) => {
  const p = auditPresentation(auditState);

  return (
    <Card
      className={`shadow-none mb-3 border ${p.prompting ? `border-${p.tone}` : "border-300"}`}
      style={p.prompting ? { borderWidth: 2 } : undefined}
    >
      <Card.Body className={`p-3 ${p.prompting ? `bg-${p.tone}-subtle` : ""}`}>
        <Row className="align-items-center g-3">
          <Col xs={12} md>
            <div className="d-flex align-items-start gap-2">
              <FontAwesomeIcon icon={p.icon} className={`text-${p.tone} mt-1`} />
              <div>
                <div className={`fw-bold fs-9 ${p.prompting ? `text-${p.tone}` : "text-900"}`}>
                  {skipped ? "Audit skipped for now" : p.title}
                  {auditState?.alwaysSkippable && (
                    <SubtleBadge bg="secondary" pill className="ms-2 fs-11 align-middle">
                      Never blocks
                    </SubtleBadge>
                  )}
                </div>
                <p className="text-700 mb-0 fs-10">
                  {skipped ? "You can come back to it whenever you're ready." : p.text}
                </p>
              </div>
            </div>
          </Col>

          <Col xs={12} md="auto">
            <div className="d-flex flex-wrap gap-3 text-center">
              <div>
                <div className="fw-bold fs-9 text-900">{formatDate(auditState?.dueDate)}</div>
                <div className="text-600 fs-11">Due</div>
              </div>
              <div>
                <div className="fw-bold fs-9 text-900">{formatDate(auditState?.lastAuditAt)}</div>
                <div className="text-600 fs-11">Last audit</div>
              </div>
              {auditState?.intervalDays > 0 && (
                <div>
                  <div className="fw-bold fs-9 text-900">{auditState.intervalDays}d</div>
                  <div className="text-600 fs-11">Every</div>
                </div>
              )}
            </div>
          </Col>

          <Col xs={12} md="auto" className="d-flex flex-wrap gap-2">
            {p.canSkip && !skipped && started && (
              <Button variant={`outline-${p.tone}`} size="sm" className="rounded-pill px-3" onClick={onSkip}>
                <FontAwesomeIcon icon={faForward} className="me-1" />
                Skip for now
              </Button>
            )}
            {!started && (
              <Button variant="primary" size="sm" className="rounded-pill px-3" onClick={onStart}>
                <FontAwesomeIcon icon={faClipboardCheck} className="me-1" />
                {skipped ? "Start audit" : p.prompting ? "Start audit" : "Run an audit"}
              </Button>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              className="border-300"
              onClick={onRefresh}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faSync} spin={loading} className="me-1" />
              Refresh
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

AuditStateBanner.propTypes = {
  auditState: PropTypes.object,
  loading: PropTypes.bool,
  onRefresh: PropTypes.func.isRequired,
  skipped: PropTypes.bool,
  onSkip: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
  started: PropTypes.bool
};

/* ------------------------------------------------------------------ */
/* Photo upload (INVUI-704) — capture is the phone's job               */
/* ------------------------------------------------------------------ */

const AuditPhoto = ({ patientId, image, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const choose = () => inputRef.current && inputRef.current.click();

  const handleFile = async (file) => {
    if (!file) return;
    setError("");

    if (!String(file.type || "").startsWith("image/")) {
      setError("Please choose an image file (JPG, PNG or similar).");
      return;
    }
    if (file.size > MAX_AUDIT_IMAGE_BYTES) {
      setError(`That image is too large — the limit is ${Math.round(MAX_AUDIT_IMAGE_BYTES / 1048576)} MB.`);
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setUploading(true);
    try {
      const uploaded = await uploadAuditImage(file, patientId);
      if (!uploaded.imageId) {
        URL.revokeObjectURL(previewUrl);
        setError("The photo uploaded but the server didn't return a reference for it. Please try again.");
        return;
      }
      if (image?.previewUrl) URL.revokeObjectURL(image.previewUrl);
      onChange({ imageId: uploaded.imageId, previewUrl, name: file.name });
    } catch (err) {
      URL.revokeObjectURL(previewUrl);
      setError(errorMessage(err, "The photo could not be uploaded."));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Card className="border border-300 shadow-none mt-3">
      <Card.Body className="p-3">
        <Row className="align-items-center g-3">
          <Col xs="auto">
            <div
              className="rounded-3 border border-300 bg-body-tertiary d-flex align-items-center justify-content-center overflow-hidden"
              style={{ width: 96, height: 72 }}
            >
              {image?.previewUrl ? (
                <img
                  src={image.previewUrl}
                  alt="Audit stock"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <FontAwesomeIcon icon={faImage} className="text-400 fs-6" />
              )}
            </div>
          </Col>
          <Col>
            <div className="fw-bold text-900 fs-10">
              Photo of the counted stock <span className="text-danger">*</span>
            </div>
            <div className="text-600 fs-11">
              {image?.imageId
                ? `Uploaded: ${image.name || "photo"}`
                : "Required before the audit can be submitted. Take it on a phone, then upload it here."}
            </div>
            {error && (
              <div className="text-danger fs-11 mt-1">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                {error}
              </div>
            )}
          </Col>
          <Col xs={12} sm="auto">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="d-none"
              onChange={(e) => handleFile(e.target.files && e.target.files[0])}
            />
            <Button
              variant={image?.imageId ? "outline-secondary" : "outline-primary"}
              size="sm"
              className="rounded-pill px-3"
              onClick={choose}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={image?.imageId ? faRedo : faCamera} className="me-1" />
                  {image?.imageId ? "Replace photo" : "Upload photo"}
                </>
              )}
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

AuditPhoto.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  image: PropTypes.object,
  onChange: PropTypes.func.isRequired
};

/* ------------------------------------------------------------------ */
/* Main — checklist, counts, submit (INVUI-703)                        */
/* ------------------------------------------------------------------ */

const PatientAudit = ({ patientId, auditState, stateLoading, onRefreshState, onSubmitted }) => {
  const { labelOf } = useScheduleTokens();
  const presentation = auditPresentation(auditState);

  const [started, setStarted] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [lines, setLines] = useState([]);
  const [counts, setCounts] = useState({}); // key -> { counted_qty, reason }
  const [page, setPage] = useState(0);
  const [image, setImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // The photo preview is a local object URL; release it when this screen goes away.
  const imageRef = useRef(null);
  useEffect(() => {
    imageRef.current = image;
  }, [image]);
  useEffect(
    () => () => {
      if (imageRef.current?.previewUrl) URL.revokeObjectURL(imageRef.current.previewUrl);
    },
    []
  );

  // A due or overdue audit opens straight away; otherwise it waits to be started.
  useEffect(() => {
    if (presentation.prompting && !skipped) setStarted(true);
  }, [presentation.prompting, skipped]);

  const loadChecklist = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { lines: rows } = await getAuditChecklist(patientId);
      setLines(rows);
      setCounts({});
      setPage(0);
    } catch (err) {
      setLoadError(errorMessage(err, "The audit checklist could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (started) loadChecklist();
  }, [started, loadChecklist]);

  const setLine = (key, patch) => {
    setSubmitError("");
    setCounts((prev) => ({ ...prev, [key]: { ...(prev[key] || { counted_qty: "", reason: "" }), ...patch } }));
  };

  const view = useMemo(
    () =>
      lines.map((line) => {
        const entry = counts[line.key] || { counted_qty: "", reason: "" };
        const counted = entry.counted_qty;
        const hasCount = counted !== "" && counted !== null && Number.isFinite(Number(counted));
        // A line with no balance row has no baseline, so a count can't be a variance.
        const variance =
          hasCount && !line.no_balance_row ? Number(counted) - Number(line.expected_qty) : null;
        return { ...line, counted_qty: counted, reason: entry.reason, hasCount, variance };
      }),
    [lines, counts]
  );

  const countedCount = view.filter((l) => l.hasCount).length;
  const pageCount = Math.max(1, Math.ceil(view.length / PAGE_SIZE));
  const pageRows = view.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const problems = useMemo(() => {
    const list = [];
    const uncounted = view.filter((l) => !l.hasCount).length;
    if (uncounted > 0) list.push(`${uncounted} medication${uncounted > 1 ? "s" : ""} still to count.`);
    view
      .filter((l) => l.hasCount && Number(l.counted_qty) < 0)
      .forEach((l) => list.push(`${l.drug_name}: the count can't be negative.`));
    view
      .filter((l) => l.variance !== null && l.variance !== 0 && !String(l.reason || "").trim())
      .forEach((l) => list.push(`${l.drug_name}: count differs from the record — a reason is required.`));
    if (!image?.imageId) list.push("Upload a photo of the counted stock.");
    return list;
  }, [view, image]);

  const handleSubmit = async () => {
    if (problems.length > 0) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const message = await submitAudit(patientId, {
        imageId: image.imageId,
        lines: view.map((l) => ({
          prescription_id: l.prescription_id,
          pharmacy_order_id: l.pharmacy_order_id,
          expected_qty: l.expected_qty,
          counted_qty: Number(l.counted_qty),
          reason: l.reason
        }))
      });
      toast.success(message);
      setCounts({});
      if (image?.previewUrl) URL.revokeObjectURL(image.previewUrl);
      setImage(null);
      setStarted(false);
      setSkipped(false);
      if (onRefreshState) onRefreshState();
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setSubmitError(errorMessage(err, "The audit could not be submitted."));
    } finally {
      setSubmitting(false);
    }
  };

  const goToFirstProblem = () => {
    const idx = view.findIndex(
      (l) => !l.hasCount || (l.variance !== null && l.variance !== 0 && !String(l.reason || "").trim())
    );
    if (idx >= 0) setPage(Math.floor(idx / PAGE_SIZE));
  };

  return (
    <div className="pt-2">
      <AuditStateBanner
        auditState={auditState}
        loading={stateLoading}
        onRefresh={() => onRefreshState && onRefreshState()}
        skipped={skipped}
        started={started}
        onSkip={() => {
          setSkipped(true);
          setStarted(false);
        }}
        onStart={() => {
          setSkipped(false);
          setStarted(true);
        }}
      />

      {started && (
        <>
          <Card className="border border-300 shadow-none">
            <Card.Header className="bg-body-tertiary border-bottom border-300 py-3">
              <Row className="g-2 align-items-center">
                <Col xs={12} md>
                  <h6 className="mb-1 fw-bold text-900 fs-9">
                    <FontAwesomeIcon icon={faClipboardCheck} className="text-primary me-2" />
                    Count every tracked medication
                  </h6>
                  <p className="text-600 mb-0 fs-10">
                    Enter what is physically on hand. If it differs from the record, say why.
                  </p>
                </Col>
                <Col xs={12} md="auto">
                  <SubtleBadge
                    bg={view.length > 0 && countedCount === view.length ? "success" : "secondary"}
                    pill
                    className="d-inline-block fs-10"
                  >
                    Counted {countedCount} of {view.length}
                  </SubtleBadge>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" role="status" />
                  <p className="mt-3 text-600 fs-10 mb-0">Loading the audit checklist...</p>
                </div>
              ) : loadError ? (
                <Alert variant="danger" className="d-flex align-items-start border-0 rounded-0 fs-10 mb-0">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-3 mt-1 fs-9" />
                  <div className="flex-1">
                    <div className="fw-bold">We couldn&apos;t load the checklist</div>
                    <div>{loadError}</div>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="ms-3 rounded-pill px-3"
                    onClick={loadChecklist}
                  >
                    Retry
                  </Button>
                </Alert>
              ) : view.length === 0 ? (
                <div className="text-center py-5 px-3">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-success fs-5 mb-3" />
                  <h6 className="text-900 fw-bold mb-1 fs-9">Nothing to audit</h6>
                  <p className="text-600 fs-10 mb-0">
                    This patient has no tracked medications to count.
                  </p>
                </div>
              ) : (
                <Table responsive hover className="mb-0 fs-10 align-middle">
                  <thead>
                    <tr className="bg-200 text-900">
                      <th className="ps-3 fs-11 text-uppercase fw-semibold">Medication</th>
                      <th className="fs-11 text-uppercase fw-semibold">Schedule</th>
                      <th className="fs-11 text-uppercase fw-semibold text-center">On record</th>
                      <th className="fs-11 text-uppercase fw-semibold text-center" style={{ minWidth: 110 }}>
                        Counted
                      </th>
                      <th className="fs-11 text-uppercase fw-semibold text-center">Difference</th>
                      <th className="pe-3 fs-11 text-uppercase fw-semibold" style={{ minWidth: 220 }}>
                        Reason
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((line) => {
                      const needsReason = line.variance !== null && line.variance !== 0;
                      return (
                        <tr key={line.key}>
                          <td className="ps-3">
                            <div className="fw-semibold text-900">{line.drug_name}</div>
                            <div className="text-600 fs-11">
                              {[line.strength, line.rx_number ? `Rx ${line.rx_number}` : ""]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          </td>
                          <td>
                            {line.schedule ? (
                              <ScheduleBadge token={line.schedule} title={labelOf(line.schedule)} />
                            ) : (
                              <span className="text-500">—</span>
                            )}
                          </td>
                          <td className="text-center">
                            {line.no_balance_row ? (
                              <SubtleBadge bg="secondary" pill className="d-inline-block fs-11">
                                No stock recorded
                              </SubtleBadge>
                            ) : (
                              <span className="fw-semibold text-900">{formatQty(line.expected_qty)}</span>
                            )}
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              type="number"
                              min="0"
                              step="any"
                              className="text-center"
                              placeholder="Count"
                              value={line.counted_qty}
                              isInvalid={line.hasCount && Number(line.counted_qty) < 0}
                              onChange={(e) =>
                                setLine(line.key, {
                                  counted_qty: e.target.value === "" ? "" : e.target.value
                                })
                              }
                            />
                          </td>
                          <td className="text-center">
                            {line.no_balance_row ? (
                              line.hasCount ? (
                                <span className="text-600 fs-11">Sets starting stock</span>
                              ) : (
                                <span className="text-500">—</span>
                              )
                            ) : line.variance === null ? (
                              <span className="text-500">—</span>
                            ) : line.variance === 0 ? (
                              <SubtleBadge bg="success" pill className="d-inline-block">
                                Matches
                              </SubtleBadge>
                            ) : (
                              <SubtleBadge
                                bg={line.variance < 0 ? "danger" : "warning"}
                                pill
                                className="d-inline-block"
                              >
                                {line.variance > 0 ? "+" : ""}
                                {formatQty(line.variance)}
                              </SubtleBadge>
                            )}
                          </td>
                          <td className="pe-3">
                            <Form.Control
                              size="sm"
                              placeholder={needsReason ? "Required — why is it different?" : "Optional"}
                              value={line.reason}
                              onChange={(e) => setLine(line.key, { reason: e.target.value })}
                              isInvalid={needsReason && !String(line.reason || "").trim()}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>

            {view.length > PAGE_SIZE && (
              <Card.Footer className="bg-body-tertiary border-top border-300">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 fs-10">
                  <span className="text-600">
                    {page * PAGE_SIZE + 1}–{Math.min(view.length, (page + 1) * PAGE_SIZE)} of {view.length}
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      size="sm"
                      variant={page > 0 ? "primary" : "tertiary"}
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
                      Previous
                    </Button>
                    <span className="text-700">
                      Page {page + 1} of {pageCount}
                    </span>
                    <Button
                      size="sm"
                      variant={page < pageCount - 1 ? "primary" : "tertiary"}
                      disabled={page >= pageCount - 1}
                      onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    >
                      Next
                      <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                    </Button>
                  </div>
                </div>
              </Card.Footer>
            )}
          </Card>

          {view.length > 0 && !loading && !loadError && (
            <>
              <AuditPhoto patientId={patientId} image={image} onChange={setImage} />

              {problems.length > 0 && (
                <Alert variant="warning" className="border-0 rounded-3 mt-3 fs-10 mb-0">
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                    <div>
                      <div className="fw-bold mb-1">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        Before you can submit
                      </div>
                      <ul className="mb-0 ps-4">
                        {problems.slice(0, 6).map((p) => (
                          <li key={p}>{p}</li>
                        ))}
                        {problems.length > 6 && <li>…and {problems.length - 6} more.</li>}
                      </ul>
                    </div>
                    {view.length > PAGE_SIZE && (
                      <Button variant="link" size="sm" className="p-0 fs-10" onClick={goToFirstProblem}>
                        Go to the first one
                      </Button>
                    )}
                  </div>
                </Alert>
              )}

              {submitError && (
                <Alert variant="danger" className="d-flex align-items-start border-0 rounded-3 mt-3 fs-10 mb-0">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-3 mt-1 fs-9" />
                  <div>
                    <div className="fw-bold">Nothing was saved</div>
                    <div>The audit wasn&apos;t recorded. Your counts are still here — fix the problem and submit again.</div>
                    <div className="text-700 mt-1">Details: {submitError}</div>
                  </div>
                </Alert>
              )}

              <div
                className="d-flex flex-wrap justify-content-between align-items-center gap-2 p-3 mt-3 rounded-3 border border-300 bg-body-tertiary"
                style={{ position: "sticky", bottom: 0, zIndex: 5 }}
              >
                <span className="fs-10 text-600">
                  {countedCount} of {view.length} counted
                  {image?.imageId ? " · photo added" : " · photo needed"}
                </span>
                <Button
                  variant="primary"
                  className="px-4 fw-semibold rounded-3"
                  onClick={handleSubmit}
                  disabled={submitting || problems.length > 0}
                >
                  {submitting ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="me-1" />
                      Submit audit
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

PatientAudit.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  auditState: PropTypes.object,
  stateLoading: PropTypes.bool,
  onRefreshState: PropTypes.func,
  onSubmitted: PropTypes.func
};

export default PatientAudit;
